import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { Check, KeyRound, Repeat } from 'lucide-react'

import type { PatchTenantSettingsRequest, TenantSettingsResponse } from '@cattle/contracts'

import {
  Button,
  Field,
  LoadingSkeleton,
  LockChip,
  Money,
  MoneyInput,
  PageHeader,
  PinSheet,
  QueryBoundary,
  RadioCards,
  RelativeTime,
  Section,
  StatusBadge,
  TextInput,
  Toggle,
  usePriceLock,
  useToast,
  type RadioCardOption,
} from '../../../components'
import { useOwnerSettings, type OwnerSettings } from '../../../fixtures'
import { ApiRequestError, patch } from '../../../lib/apiClient'
import { formatDuration } from '../../../lib/format'
import { t } from '../../../lib/i18n'

/**
 * O-06 · Settings.
 *
 * Every field states what it changes DOWNSTREAM — that is this screen's rule,
 * which is why every row is a `Field layout="row"` with a hint. Two of these
 * settings move a figure on every animal in the book: the sell commission moves
 * every break-even, and the feed estimate moves every day no real bill covers.
 * Both say so on the screen where they are changed.
 */

/** Basis points, not money and not a fraction: 200 bps is 2.0%. */
const bpsToPercent = (bps: number): string => (bps / 100).toFixed(1)

/**
 * And back. A percentage typed by a person is display; basis points are what is
 * stored, because 2.5% in a float is not 2.5% and a commission that drifts by a
 * rounding error moves a break-even on every animal in the book.
 */
function percentToBps(value: string): number | undefined {
  const parsed = Number(value.trim())
  if (!Number.isFinite(parsed) || parsed < 0) return undefined
  return Math.round(parsed * 100)
}

export function OwnerSettingsScreen() {
  const settings = useOwnerSettings()

  return (
    <QueryBoundary
      query={settings}
      skeleton={
        <div className="grid gap-3.5 lg:grid-cols-2">
          <LoadingSkeleton variant="panel" />
          <LoadingSkeleton variant="panel" />
        </div>
      }
    >
      {(data) => <SettingsForm settings={data} />}
    </QueryBoundary>
  )
}

function SettingsForm({ settings }: { settings: OwnerSettings }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { gate } = usePriceLock()
  const nameId = useId()
  const buyId = useId()
  const sellId = useId()
  const otherId = useId()
  const feedId = useId()

  const [pinOpen, setPinOpen] = useState(false)
  const [name, setName] = useState(settings.businessName)
  const [grouping, setGrouping] = useState(settings.grouping)
  const [priceLockOn, setPriceLockOn] = useState(settings.priceLockOn)
  const [buyPercent, setBuyPercent] = useState(bpsToPercent(settings.buyCommissionBps))
  const [sellPercent, setSellPercent] = useState(bpsToPercent(settings.sellCommissionBps))
  const [otherSaleCostsPaisa, setOtherSaleCostsPaisa] = useState<number | undefined>(
    settings.otherSaleCostsPaisa,
  )
  const [feedEstimatePaisa, setFeedEstimatePaisa] = useState<number | undefined>(
    settings.feedEstimatePaisa,
  )

  /** A changed sell commission re-quotes every animal being held, not just the next. */
  const sellCommissionChanged = sellPercent !== bpsToPercent(settings.sellCommissionBps)

  /*
    An emptied box means "unchanged", so the check still compares against a real
    rate. Both figures are money and money may be ABSENT from the payload — under
    Price Lock the server does not send them at all — so the gap is only a number
    when both halves are. `undefined` renders as `••••••`, never as Rs 0.
  */
  const actual = settings.actualPerAnimalPerDayPaisa
  const assumed = feedEstimatePaisa ?? settings.feedEstimatePaisa
  const gap = actual !== undefined && assumed !== undefined ? actual - assumed : undefined

  /*
    One PATCH, and only the fields that actually changed — `patchTenantSettings`
    refuses an empty body, and sending every field back would write an audit row
    for each one. `actualPerAnimalPerDayPaisa` is deliberately not here: it is a
    measurement the engine derives, not a setting a client may state.
  */
  const save = useMutation({
    mutationFn: async () => {
      const body: PatchTenantSettingsRequest = {}
      if (name.trim() !== settings.businessName) body.businessName = name.trim()
      if (grouping !== settings.grouping) body.currencyGrouping = grouping
      if (priceLockOn !== settings.priceLockOn) body.priceLockEnabled = priceLockOn

      const buyBps = percentToBps(buyPercent)
      if (buyBps !== undefined && buyBps !== settings.buyCommissionBps) {
        body.defaultBuyCommissionBps = buyBps
      }
      const sellBps = percentToBps(sellPercent)
      if (sellBps !== undefined && sellBps !== settings.sellCommissionBps) {
        body.defaultSellCommissionBps = sellBps
      }
      if (
        otherSaleCostsPaisa !== undefined &&
        otherSaleCostsPaisa !== settings.otherSaleCostsPaisa
      ) {
        body.expectedSaleCostsPaisa = otherSaleCostsPaisa
      }
      if (feedEstimatePaisa !== undefined && feedEstimatePaisa !== settings.feedEstimatePaisa) {
        body.defaultDailyRatePaisa = feedEstimatePaisa
      }

      if (Object.keys(body).length === 0) return null
      return patch<TenantSettingsResponse>('/settings', body)
    },
    onSuccess: async () => {
      // A rate change is retroactive by construction (BR §5), so every figure
      // in the book has just moved. Nothing cached survives it.
      await queryClient.invalidateQueries()
      toast.show(t('act.saveChanges'))
    },
    onError: (cause: unknown) =>
      toast.show(
        cause instanceof ApiRequestError ? cause.message : t('state.errorTitle'),
        'danger',
      ),
  })

  const groupingOptions: RadioCardOption<OwnerSettings['grouping']>[] = [
    { value: 'indian', title: t('o.settings.numberFormatIndian'), points: [] },
    { value: 'western', title: t('o.settings.numberFormatWestern'), points: [] },
  ]

  const percentField = (
    id: string,
    value: string,
    onChange: (next: string) => void,
  ) => (
    <div className="flex items-center gap-2">
      <TextInput id={id} value={value} onChange={onChange} className="max-w-[90px] tnum" />
      <span className="font-display text-[13px] font-bold text-muted">%</span>
    </div>
  )

  return (
    <>
      <PageHeader
        title={t('o.settings.title')}
        subtitle={t('o.settings.subtitle')}
        actions={
          <>
            <LockChip onRequestUnlock={() => setPinOpen(true)} />
            <Button
              variant="primary"
              icon={<Check className="size-4" />}
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {t('act.saveChanges')}
            </Button>
          </>
        }
      />

      <div className="grid gap-3.5 lg:grid-cols-2">
        <div className="flex flex-col gap-3.5">
          <Section title={t('o.settings.yourBusiness')}>
            <Field
              layout="row"
              label={t('label.name')}
              hint={t('o.settings.nameHint')}
              htmlFor={nameId}
            >
              <TextInput id={nameId} value={name} onChange={setName} />
            </Field>
            <Field
              layout="row"
              label={t('o.settings.numberFormat')}
              hint={t('o.settings.numberFormatHint')}
            >
              <RadioCards
                name="number-format"
                options={groupingOptions}
                value={grouping}
                onChange={setGrouping}
                className="max-w-[320px]"
              />
            </Field>
          </Section>

          <Section
            title={t('o.settings.priceLock')}
            action={
              priceLockOn ? (
                <StatusBadge tone="ok">{t('label.on')}</StatusBadge>
              ) : (
                <StatusBadge tone="neutral">{t('label.off')}</StatusBadge>
              )
            }
          >
            <div className="border-b border-muted/15 py-[15px]">
              <Toggle
                checked={priceLockOn}
                onChange={setPriceLockOn}
                label={t('o.settings.hidePrices')}
                hint={t('o.settings.priceLockHint')}
              />
            </div>
            <Field
              layout="row"
              label={t('o.settings.hideAgain')}
              hint={t('o.settings.hideAgainHint')}
            >
              <span className="inline-flex h-10 items-center rounded-[10px] border border-glass-border bg-glass px-3.5 text-[13.5px] font-semibold tnum text-dark">
                {/* `autoLockSeconds`, the tenant's own setting — not the five
                    minutes the design deck happened to be showing. */}
                {formatDuration(settings.autoLockSeconds)}
              </span>
            </Field>
            <Field
              layout="row"
              label={t('o.settings.yourPin')}
              hint={
                /*
                  `pinChangedAt` is in the settings payload. The date itself is
                  rendered by <RelativeTime> rather than formatted into t(): the
                  comparison is against the server's day in Karachi, and a book
                  with no PIN yet is a different sentence from one whose PIN was
                  changed at some unrecorded time.
                */
                settings.pinChangedAt !== null ? (
                  <>
                    {t('o.settings.pinChanged')} <RelativeTime date={settings.pinChangedAt} />
                  </>
                ) : settings.pinSet ? (
                  t('o.settings.pinNeverChanged')
                ) : (
                  t('o.settings.pinNotSet')
                )
              }
            >
              <Button icon={<KeyRound className="size-4" />} onClick={() => setPinOpen(true)}>
                {t('o.settings.changePin')}
              </Button>
            </Field>
          </Section>
        </div>

        <div className="flex flex-col gap-3.5">
          <Section title={t('o.settings.commission')}>
            <Field
              layout="row"
              label={t('o.settings.buyCommission')}
              hint={t('o.settings.buyCommissionHint')}
              htmlFor={buyId}
            >
              {percentField(buyId, buyPercent, setBuyPercent)}
            </Field>
            <Field
              layout="row"
              label={t('o.settings.sellCommission')}
              hint={t('o.settings.sellCommissionHint')}
              htmlFor={sellId}
            >
              {percentField(sellId, sellPercent, setSellPercent)}
            </Field>
            <Field
              layout="row"
              label={t('o.settings.otherSaleCosts')}
              hint={t('o.settings.otherSaleCostsHint')}
              htmlFor={otherId}
            >
              <MoneyInput
                id={otherId}
                paisa={otherSaleCostsPaisa}
                onChange={setOtherSaleCostsPaisa}
                ariaLabel={t('o.settings.otherSaleCosts')}
              />
            </Field>

            {sellCommissionChanged ? (
              <p
                role="status"
                className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn"
              >
                {t('o.settings.sellCommissionWarning')}
              </p>
            ) : null}

            <p className="mt-2.5 text-xs leading-relaxed text-muted">
              {t('o.settings.commissionNote')}
            </p>
          </Section>

          <Section title={t('o.settings.feed')}>
            <Field
              layout="row"
              label={t('o.settings.dailyEstimate')}
              hint={t('o.settings.dailyEstimateHint')}
              htmlFor={feedId}
            >
              <div className="flex items-center gap-2">
                <MoneyInput
                  id={feedId}
                  paisa={feedEstimatePaisa}
                  onChange={setFeedEstimatePaisa}
                  ariaLabel={t('o.settings.dailyEstimate')}
                />
                <span className="font-display text-xs font-bold text-muted">
                  {t('unit.perDay')}
                </span>
              </div>
            </Field>
            <Field
              layout="row"
              label={t('o.settings.realBills')}
              hint={t('o.settings.realBillsHint')}
            >
              <Button
                icon={<Repeat className="size-4" />}
                onClick={() => toast.show(t('o.nav.monthlyCosts'), 'info')}
              >
                {t('o.nav.monthlyCosts')}
              </Button>
            </Field>

            {/*
              The live reality check. `actual` is a book figure derived from the
              real feed bills, so it goes through `gate()` like any other; the
              estimate above it is a setting being edited, not a book figure.
            */}
            <p className="mt-3 text-xs leading-relaxed text-muted">
              {t('o.settings.realityCheck')}{' '}
              <Money paisa={gate(actual)} className="text-[12px]" />{' '}
              {gap === undefined ? null : gap === 0 ? (
                t('o.settings.realityCheckMatch')
              ) : (
                <>
                  {t('o.settings.realityCheckPerDay')}{' '}
                  <Money paisa={gate(Math.abs(gap))} className="text-[12px]" />{' '}
                  {gap > 0
                    ? t('o.settings.realityCheckMore')
                    : t('o.settings.realityCheckLess')}
                </>
              )}
            </p>
          </Section>
        </div>
      </div>

      <PinSheet open={pinOpen} onClose={() => setPinOpen(false)} />
    </>
  )
}
